import styled from '@emotion/styled';
import Accordion from '@base/Accordion/Accordion';
import { useEffect, useState, useRef } from 'react';
import { Spacings, Fonts, mediaQuery } from '@styles/index';
import H4 from '@base/H4/H4';
import H7 from '@base/H7/H7';
import Image from '@base/Image';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { SWRConfig } from 'swr';
import { segment } from '@actions/analytics';
import useRequestStatusStore from '@stores/requestStatus';
import { getFetchingUpdater } from '@stores/selectors/requestStatus';
import useCurriculum from '../useCurriculum';
import CurriculumSection, {
  getHasNextUrl,
  ViewMoreMultipleSection
} from './CurriculumSection';
import { BATCH_SCHEDULE_EXPAND_CLICKED } from '../constants/events';

const CurriculumContainer = styled.div`
  grid-column: span 16;
`;

const AccordionWrapper = styled.div`
  margin-top: ${Spacings.SPACING_0B};
`;

const StyledAccordion = styled(Accordion)`
  &.MuiAccordion-root {
    padding: ${Spacings.SPACING_4B} ${Spacings.SPACING_8B};
    margin: ${Spacings.SPACING_0B};
    margin-top: ${Spacings.SPACING_6B};
    background: var(--color-base-1);
    box-shadow: ${boxShadowGenerator()};
    border-radius: ${Spacings.SPACING_2B};
  }
  ${mediaQuery.uptoMobile} {
    &.MuiAccordion-root {
      padding: ${Spacings.SPACING_4B};
      border-radius: ${Spacings.SPACING_2B};
      margin-bottom: ${Spacings.SPACING_4B};
    }
  }
`;

const StyledAccordionSummary = styled(Accordion.Summary)`
  font-size: ${Fonts.P1};
  line-height: ${Fonts.PARA_LINE_HEIGHT};
`;

const StyledAccordionDetails = styled(Accordion.Details)`
  font-size: ${Fonts.P1};
  line-height: ${Fonts.PARA_LINE_HEIGHT};
  color: var(--color-i-gray);
`;

const StyledH4 = styled(H4)`
  font-size: ${Fonts.H4};
  font-weight: ${Fonts.HEAVY};
  line-height: ${Spacings.SPACING_6B};
  text-align: left;
`;

const StyledTextWrapper = styled(H7)`
  font-weight: ${Fonts.REGULAR};
  color: var(--color-text-secondary);
  font-size: ${Fonts.SMALL};
  line-height: ${Spacings.SPACING_5B};
  margin-top: ${Spacings.SPACING_1B};
`;

const LoadMoreCourses = styled(ViewMoreMultipleSection)`
  height: 70px;
  text-align: center;
  padding: ${Spacings.SPACING_6B};
  box-shadow: ${boxShadowGenerator()};
  margin-top: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    height: 56px;
    padding: ${Spacings.SPACING_18} ${Spacings.SPACING_0B};
  }
`;

const Curriculum = (props) => {
  const {
    isSubscribed,
    lastPrimarySourceSection,
    goalUID,
    selectedLesson,
    batch,
    onItemClick,
    setAnchorEl,
    batchGroupCourses,
    batchGroupUID,
    classType,
    segmentData
  } = props;

  const { schedule = [], count, url, limit } = batchGroupCourses;
  const [expandedCourse, setExpandedCourse] = useState({
    [schedule?.[0]?.lessonGroupId]: true
  });

  const setFetching = useRequestStatusStore(getFetchingUpdater);

  const curriculum = useCurriculum(url, limit, classType);
  const {
    setSize: setNumberOfPages,
    curriculumData,
    isValidating
  } = curriculum;

  const { schedule: results = [] } = curriculumData;
  const coursesToRender = results?.length ? results : schedule;
  const hasNextUrl = getHasNextUrl(count, coursesToRender);
  const containerRef = useRef(null);

  const scrollToContainerRef = () => {
    containerRef.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
  };

  useEffect(() => {
    if (isValidating) {
      setFetching(true);
    } else {
      setFetching(false);
    }
  }, [setFetching, isValidating]);

  const loadMoreCourses = () => {
    if (!isValidating && hasNextUrl) {
      setNumberOfPages((nPages) => nPages + 1);
    }
  };

  return (
    <SWRConfig
      value={{
        revalidateOnMount: false
      }}
    >
      <CurriculumContainer ref={containerRef}>
        <AccordionWrapper>
          {coursesToRender?.map((course) => {
            const levelId = course.lessonGroupId;
            const isExpanded = expandedCourse && expandedCourse[levelId];
            const handleClick = () => {
              if (isExpanded) {
                setExpandedCourse({ [levelId]: false });
              } else {
                scrollToContainerRef();
                setExpandedCourse({ [levelId]: true });
                segment.track(BATCH_SCHEDULE_EXPAND_CLICKED, {
                  ...segmentData,
                  last_primary_source_section: lastPrimarySourceSection
                });
              }
            };
            return (
              <StyledAccordion expanded={isExpanded} key={course.lessonGroupId}>
                <StyledAccordionSummary
                  $isExpanded={isExpanded}
                  expandIcon={
                    <Image
                      src="batch-group/chevron-down.svg"
                      alt="expand"
                      width={24}
                      height={24}
                    />
                  }
                  onClick={handleClick}
                >
                  <StyledH4>{course.lessonGroupName}</StyledH4>
                  <StyledTextWrapper>{course.classCountText}</StyledTextWrapper>
                </StyledAccordionSummary>
                <StyledAccordionDetails id={`Acc-${course.levelId}`}>
                  <CurriculumSection
                    batch={batch}
                    selectedLesson={selectedLesson}
                    lastPrimarySourceSection={lastPrimarySourceSection}
                    onItemClick={onItemClick}
                    isSubscribed={isSubscribed}
                    setPdfAnchorEl={setAnchorEl}
                    goalUID={goalUID}
                    course={course}
                    isMultipleCourses
                    batchGroupUID={batchGroupUID}
                    classType={classType}
                    isFetchData={isExpanded}
                    segmentData={segmentData}
                  />
                </StyledAccordionDetails>
              </StyledAccordion>
            );
          })}
        </AccordionWrapper>
        {hasNextUrl && (
          <LoadMoreCourses onClick={loadMoreCourses}>View more</LoadMoreCourses>
        )}
      </CurriculumContainer>
    </SWRConfig>
  );
};

export default Curriculum;
